/**
 * VideoCallContent - The actual video call UI content
 * Can be rendered in popup window or inline
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
    VideoTileGrid,
    useRemoteVideoTileState,
    useLocalVideo,
    useAttendeeStatus,
    useRosterState,
    useContentShareState,
    useActiveSpeakersState,
    LocalVideo,
    RemoteVideo,
    ContentShare,
    useMeetingManager,
    useMeetingStatus,
    useToggleLocalMute,
    useContentShareControls,
    MeetingStatus
} from 'amazon-chime-sdk-component-library-react';
import { MeetingSessionConfiguration } from 'amazon-chime-sdk-js';
import CallControls from './CallControls';
import ParticipantsPanel from './ParticipantsPanel';
import InviteToCallModal from './InviteToCallModal';
import DeviceSettingsPanel from './DeviceSettingsPanel';
import CallQualityIndicator from './CallQualityIndicator';
import { useCallFullscreen } from './useCallFullscreen';
import { startCallSession, endCallSession } from './callSession';
import { authHeaders } from '../../utils/authHeaders';
import './VideoCall.css';

// API base URL
const API_BASE_URL = import.meta.env.VITE_API_URL || '';

// Meeting states that mean this session is over and nothing will arrive on it again
const TERMINAL_MEETING_STATUSES = [
    MeetingStatus.Failed,
    MeetingStatus.Ended,
    MeetingStatus.JoinedFromAnotherDevice,
    MeetingStatus.TerminalFailure
];

function VideoCallContent({
    callId,
    callType,
    sessionId,
    userId,
    userName,
    meetingData,
    attendeeData,
    socket,
    onEndCall,
    isPopup = false,
    isStage = false,
    initiatorName
}) {
    const { t } = useTranslation();
    const meetingManager = useMeetingManager();
    const meetingStatus = useMeetingStatus();
    const { toggleVideo, isVideoEnabled } = useLocalVideo();
    const { muted, toggleMute } = useToggleLocalMute();
    const { isLocalUserSharing, sharingAttendeeId } = useContentShareState();
    const { toggleContentShare } = useContentShareControls();
    const { roster } = useRosterState();
    const { isVideoEnabled: localVideoEnabled } = useLocalVideo();
    // Who is talking. On a six-person bridge the roster is a wall of names and the only
    // question anyone has is which of them is speaking right now.
    const activeSpeakers = useActiveSpeakersState();
    const { isFullscreen, isSupported: fullscreenSupported, toggle: toggleFullscreen } = useCallFullscreen();

    // State
    const [isJoined, setIsJoined] = useState(false);
    const [isRinging, setIsRinging] = useState(true);
    const [callDuration, setCallDuration] = useState(0);
    const [callStartTime, setCallStartTime] = useState(null);
    const [showParticipants, setShowParticipants] = useState(false);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [showDeviceSettings, setShowDeviceSettings] = useState(false);
    const [raisedHands, setRaisedHands] = useState([]);
    const [error, setError] = useState(null);
    const [hasEnded, setHasEnded] = useState(false);
    const [notice, setNotice] = useState(null);
    const [signallingDown, setSignallingDown] = useState(false);

    // Set while we are hanging up ourselves, so the meeting ending is not reported as the
    // other side dropping the call
    const isEndingRef = useRef(false);

    // Which call owns the meeting session. Read from a ref by the unmount cleanup below,
    // which must know what it is releasing without re-running when anything else changes.
    const sessionTokenRef = useRef(callId);
    sessionTokenRef.current = callId;

    // Join meeting on mount.
    //
    // Through callSession rather than straight onto the MeetingManager: there is now exactly
    // one MeetingManager for the whole browser session (the popup used to bring its own with
    // it), so this join can land while the PREVIOUS call's leave is still settling - and that
    // leave ends by nulling whatever session it finds, including this one. callSession
    // serialises the two and gives the session an owner. See the note at the top of that file.
    useEffect(() => {
        const joinMeeting = async () => {
            if (!meetingData || !attendeeData || isJoined) return;

            // navigator.mediaDevices is only available in secure contexts (HTTPS or localhost)
            if (!navigator.mediaDevices) {
                setError(t('aiChat.calls.mediaDevicesUnavailable'));
                return;
            }

            try {
                const meetingSessionConfiguration = new MeetingSessionConfiguration(
                    meetingData,
                    attendeeData
                );
                await startCallSession(meetingManager, callId, meetingSessionConfiguration);
                setIsJoined(true);
                setIsRinging(false);
                setCallStartTime(Date.now());
            } catch (err) {
                console.error('[VideoCall] Error joining meeting:', err);
                setError(err.message);
            }
        };

        joinMeeting();
    }, [meetingData, attendeeData, meetingManager, callId, isJoined, t]);

    // Leave the meeting if this component goes away without an explicit hang-up. Nothing else
    // releases the camera and microphone on that path.
    //
    // Released by token: a teardown for a call that is already over must not tear down the
    // call that replaced it. Consecutive calls in one session make that reachable - this
    // cleanup is fire-and-forget, so it can land after the next call has already joined.
    useEffect(() => {
        return () => {
            endCallSession(meetingManager, sessionTokenRef.current);
        };
    }, [meetingManager]);

    // React to the session ending. The other side hanging up deletes the Chime meeting, and
    // nothing else in this window notices: the call used to sit here counting up as if it
    // were still connected.
    useEffect(() => {
        if (!isJoined || isEndingRef.current) return;
        if (TERMINAL_MEETING_STATUSES.includes(meetingStatus)) {
            setHasEnded(true);
        }
    }, [meetingStatus, isJoined]);

    // Update duration every second
    useEffect(() => {
        if (!callStartTime || hasEnded) return;

        const interval = setInterval(() => {
            setCallDuration(Math.floor((Date.now() - callStartTime) / 1000));
        }, 1000);

        return () => clearInterval(interval);
    }, [callStartTime, hasEnded]);

    // Watch the signalling socket. It belongs to the chat panel, which can be closed (or
    // simply reconnect) while this window stays up - and accept/decline/hand-raise/hang-up
    // all travel over it, so a dead socket has to be visible instead of swallowing emits.
    useEffect(() => {
        if (!socket) {
            // No socket at all is the worst case, not a quiet one: nothing this window emits
            // can reach anyone. Say so instead of leaving the banner hidden.
            setSignallingDown(true);
            return;
        }

        const handleConnect = () => setSignallingDown(false);
        const handleDisconnect = () => setSignallingDown(true);

        setSignallingDown(!socket.connected);
        socket.on('connect', handleConnect);
        socket.on('disconnect', handleDisconnect);

        return () => {
            socket.off('connect', handleConnect);
            socket.off('disconnect', handleDisconnect);
        };
    }, [socket]);

    // Listen for call accepted (stop ringing)
    useEffect(() => {
        if (!socket) return;

        const handleCallAccepted = () => {
            setIsRinging(false);
        };

        socket.on('call-accepted', handleCallAccepted);
        return () => socket.off('call-accepted', handleCallAccepted);
    }, [socket]);

    // Listen for the call being ended or declined elsewhere
    useEffect(() => {
        if (!socket) return;

        const handleCallFinished = (data) => {
            if (data?.callId && callId && data.callId !== callId) return;
            setHasEnded(true);
        };

        socket.on('call-ended', handleCallFinished);
        socket.on('call-rejected', handleCallFinished);
        return () => {
            socket.off('call-ended', handleCallFinished);
            socket.off('call-rejected', handleCallFinished);
        };
    }, [socket, callId]);

    // Show a refused call signal in THIS window. Hang up, camera/microphone changes and hand
    // raises are all emitted from here, and the chat service refuses any of them it cannot
    // attribute to a live participant. The owner's banner renders in the opener - behind this
    // window - so a refusal of something the engineer just clicked in here was invisible to
    // them. Only refusals naming this call are shown, and never a refused invite: the invite
    // modal reports that one itself, from the same refusal's acknowledgement, in this window -
    // and it does carry this callId, so it would otherwise be reported twice side by side.
    useEffect(() => {
        if (!socket) return;

        const handleCallError = (data) => {
            const { event, callId: erroredCallId, error: reason } = data || {};
            if (event === 'call-invite-user') return;
            if (!erroredCallId || !callId || String(erroredCallId) !== String(callId)) return;

            setNotice(t('aiChat.calls.callSignalRefused', {
                event: event || t('aiChat.messages.unknown'),
                reason: reason || t('aiChat.messages.unknown')
            }));
        };

        socket.on('call-error', handleCallError);
        return () => socket.off('call-error', handleCallError);
    }, [socket, callId, t]);

    // Listen for hand raise events
    useEffect(() => {
        if (!socket) return;

        const handleHandRaise = (data) => {
            const { userId: handUserId, userName: handUserName, raised, timestamp } = data;

            setRaisedHands(prev => {
                if (raised) {
                    // Add hand if not already raised
                    if (prev.some(h => h.userId === handUserId)) return prev;
                    const updated = [...prev, { userId: handUserId, userName: handUserName, timestamp }];
                    // Sort by timestamp ascending (first raised = #1)
                    updated.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
                    return updated;
                } else {
                    // Remove lowered hand
                    return prev.filter(h => h.userId !== handUserId);
                }
            });
        };

        socket.on('call-hand-raise', handleHandRaise);
        return () => socket.off('call-hand-raise', handleHandRaise);
    }, [socket]);

    // Format duration as MM:SS or HH:MM:SS
    const formattedDuration = useMemo(() => {
        const hours = Math.floor(callDuration / 3600);
        const minutes = Math.floor((callDuration % 3600) / 60);
        const seconds = callDuration % 60;

        if (hours > 0) {
            return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
        }
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }, [callDuration]);

    // Get participant count
    const participantCount = Object.keys(roster).length;

    // Handle end call
    const handleEndCall = async () => {
        isEndingRef.current = true;

        // Lower hand if raised before leaving
        if (socket && callId && raisedHands.some(h => h.userId === userId)) {
            socket.emit('call-hand-raise', {
                callId, sessionId, userId, userName, raised: false
            });
        }

        // Leave Chime meeting first (best effort). By token, so this cannot reach past its
        // own call - and idempotent with the unmount cleanup, whichever of the two runs first.
        await endCallSession(meetingManager, callId);

        // Notify backend (best effort, don't block close)
        try {
            if (callId) {
                fetch(`${API_BASE_URL}/api/ai/video-call/${callId}/end`, {
                    method: 'POST',
                    headers: authHeaders({ 'Content-Type': 'application/json' }),
                    body: JSON.stringify({ userId })
                }).catch(err => console.error('[VideoCall] Error notifying backend:', err));

                if (socket) {
                    socket.emit('call-end', {
                        callId,
                        sessionId,
                        userId,
                        userName
                    });
                }
            }
        } catch (err) {
            console.error('[VideoCall] Error in call cleanup:', err);
        }

        // Close the call window. This tree is rendered into the popup but executes in the
        // OPENER's realm, so `window` here is the opener and window.close() would be a no-op:
        // the owner (VideoCallProvider -> closeVideoCallWindow) is the only thing that can
        // close the popup, and it also unmounts this tree.
        try {
            onEndCall?.();
        } catch (err) {
            console.error('[VideoCall] Error in onEndCall callback:', err);
        }
    };

    // Toggle video
    const handleToggleVideo = useCallback(() => {
        toggleVideo();
        if (socket && callId) {
            socket.emit('call-media-update', {
                callId,
                sessionId,
                userId,
                videoEnabled: !isVideoEnabled,
                audioEnabled: !muted
            });
        }
    }, [toggleVideo, socket, callId, sessionId, userId, isVideoEnabled, muted]);

    // Toggle audio
    const handleToggleAudio = useCallback(() => {
        toggleMute();
        if (socket && callId) {
            socket.emit('call-media-update', {
                callId,
                sessionId,
                userId,
                videoEnabled: isVideoEnabled,
                audioEnabled: muted
            });
        }
    }, [toggleMute, socket, callId, sessionId, userId, isVideoEnabled, muted]);

    // Toggle screen share
    const handleToggleScreenShare = useCallback(async () => {
        setNotice(null);
        try {
            await toggleContentShare();
        } catch (err) {
            console.error('[VideoCall] Error toggling screen share:', err);
            // The call now runs in the document the click happened in, so this is usually a
            // genuinely cancelled picker rather than a refused one - but a refusal is still
            // possible (policy, no display permission on macOS), and a button that looks dead
            // is the worst outcome on the bridge where somebody needs to show a console.
            setNotice(t('aiChat.calls.screenShareFailed'));
        }
    }, [toggleContentShare, t]);

    // Toggle participants panel. The two side panels share the same strip of the stage, so
    // opening one closes the other rather than stacking them over the video.
    const toggleParticipantsPanel = useCallback(() => {
        setShowParticipants(prev => !prev);
        setShowDeviceSettings(false);
    }, []);

    const toggleDeviceSettings = useCallback(() => {
        setShowDeviceSettings(prev => !prev);
        setShowParticipants(false);
    }, []);

    // Toggle hand raise
    const handleToggleHandRaise = useCallback(() => {
        if (!socket || !callId) return;
        const isCurrentlyRaised = raisedHands.some(h => h.userId === userId);
        socket.emit('call-hand-raise', {
            callId,
            sessionId,
            userId,
            userName,
            raised: !isCurrentlyRaised
        });
    }, [raisedHands, socket, callId, sessionId, userId, userName]);

    // Open invite modal
    const openInviteModal = useCallback(() => {
        setShowInviteModal(true);
    }, []);

    // Handle invite from panel
    const handleInviteFromPanel = useCallback(() => {
        setShowParticipants(false);
        setShowInviteModal(true);
    }, []);

    // Get current participants for invite modal.
    // Chime's ExternalUserId is `user-<id>` (chime-service.createAttendee), and guests are
    // `guest-<token>` - never a bare number. parseInt on the whole string is NaN for every
    // attendee, and NaN survives a `!== null` filter, so the invite modal's
    // "already on the call" filter (currentUserIds.includes(p.id)) matched nobody and kept
    // offering people who are sitting in the call. Take the id out of the prefix instead.
    const currentParticipants = useMemo(() => {
        return Object.entries(roster).map(([attendeeId, attendee]) => {
            const match = String(attendee.externalUserId || '').match(/^user-(\d+)$/);
            return {
                id: match ? parseInt(match[1], 10) : null,
                name: attendee.name || attendee.externalUserId || t('aiChat.calls.participant')
            };
        }).filter(p => p.id !== null);
    }, [roster, t]);

    const isSomeoneSharing = !!sharingAttendeeId;

    // Chime reports active speakers loudest-first; only the first is worth highlighting,
    // because "three people are speaking" is not information anyone can act on.
    const activeSpeakerId = activeSpeakers && activeSpeakers.length ? activeSpeakers[0] : null;
    const activeSpeakerName = useMemo(() => {
        if (!activeSpeakerId) return null;
        const attendee = roster[activeSpeakerId];
        if (!attendee) return null;
        return attendee.name || attendee.externalUserId || t('aiChat.calls.participant');
    }, [activeSpeakerId, roster, t]);

    // Get remote participants from roster (attendees other than self)
    const myAttendeeId = attendeeData?.AttendeeId || attendeeData?.Attendee?.AttendeeId;
    const remoteParticipants = useMemo(() => {
        return Object.entries(roster)
            .filter(([attendeeId]) => attendeeId !== myAttendeeId)
            .map(([attendeeId, attendee]) => ({
                attendeeId,
                externalUserId: attendee.externalUserId,
                name: attendee.name || attendee.externalUserId || t('aiChat.calls.participant')
            }));
    }, [roster, myAttendeeId, t]);

    if (error) {
        return (
            <div className="video-call-error">
                <h3>{t('aiChat.calls.failedToJoin')}</h3>
                <p>{error}</p>
                <button onClick={() => onEndCall?.()}>
                    {t('aiChat.modals.close')}
                </button>
            </div>
        );
    }

    // The session is over: say so and give the engineer the one button that works, rather
    // than leaving a dead grid with a duration ticking up behind it.
    if (hasEnded) {
        return (
            <div className="video-call-error video-call-ended">
                <h3>{t('aiChat.calls.callEnded')}</h3>
                <p>{t('aiChat.calls.callDurationFinal', { duration: formattedDuration })}</p>
                <button onClick={() => onEndCall?.()}>
                    {t('aiChat.modals.close')}
                </button>
            </div>
        );
    }

    return (
        <div className={`video-call-content-wrapper ${isPopup ? 'popup-mode' : ''} ${isStage ? 'stage-mode' : ''}`}>
            {/* Header */}
            <div className="video-call-header">
                <div className="video-call-info">
                    {isRinging ? (
                        <span className="call-status ringing">
                            <span className="ringing-dot" />
                            {t('aiChat.calls.calling')}
                        </span>
                    ) : (
                        <>
                            <span className="call-type-badge">
                                {callType === 'video' ? (
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M23 7l-7 5 7 5V7z" />
                                        <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                                    </svg>
                                ) : (
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                                    </svg>
                                )}
                                {callType === 'video' ? t('aiChat.calls.videoCall') : t('aiChat.calls.audioCall')}
                            </span>
                            <span className="call-duration">{formattedDuration}</span>
                            <span className="participant-count">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                    <circle cx="9" cy="7" r="4" />
                                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                </svg>
                                {participantCount}
                            </span>
                            <CallQualityIndicator />
                            {activeSpeakerName && (
                                <span className="active-speaker-chip" aria-live="polite">
                                    <span className="active-speaker-dot" aria-hidden="true" />
                                    {t('aiChat.calls.speakingNow', { name: activeSpeakerName })}
                                </span>
                            )}
                        </>
                    )}
                </div>

                {/* The way out of full screen. Escape works too, but a call that has taken
                    over the screen has to show its own exit - not everyone knows the shortcut,
                    and this is the moment they are least able to go looking for it. */}
                {fullscreenSupported && (
                    <button
                        type="button"
                        className="call-header-btn"
                        onClick={toggleFullscreen}
                        title={isFullscreen ? t('aiChat.calls.exitFullScreen') : t('aiChat.calls.fullScreen')}
                    >
                        {isFullscreen ? (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M8 3v3a2 2 0 0 1-2 2H3M21 8h-3a2 2 0 0 1-2-2V3M3 16h3a2 2 0 0 1 2 2v3M16 21v-3a2 2 0 0 1 2-2h3" />
                            </svg>
                        ) : (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M8 3H5a2 2 0 0 0-2 2v3M21 8V5a2 2 0 0 0-2-2h-3M3 16v3a2 2 0 0 0 2 2h3M16 21h3a2 2 0 0 0 2-2v-3" />
                            </svg>
                        )}
                        <span>{isFullscreen ? t('aiChat.calls.exitFullScreen') : t('aiChat.calls.fullScreen')}</span>
                    </button>
                )}
            </div>

            {/* Signalling / action banners */}
            {signallingDown && (
                <div className="call-banner call-banner-warning">
                    {t('aiChat.calls.signallingLost')}
                </div>
            )}
            {notice && (
                <div className="call-banner call-banner-error">
                    <span>{notice}</span>
                    <button className="call-banner-dismiss" onClick={() => setNotice(null)}>
                        {t('aiChat.calls.dismiss')}
                    </button>
                </div>
            )}

            {/* Video Grid */}
            <div className={`video-call-grid-container ${isSomeoneSharing ? 'has-screen-share' : ''}`}>
                {/* Raised Hands Queue */}
                {raisedHands.length > 0 && (
                    <div className="raised-hands-queue">
                        {raisedHands.map((hand, index) => (
                            <div key={hand.userId} className="raised-hand-item">
                                <span className="hand-order">#{index + 1}</span>
                                <span className="hand-icon">{'\u270B'}</span>
                                <span className="hand-name">
                                    {String(hand.userId) === String(userId) ? t('aiChat.participants.you') : hand.userName}
                                </span>
                            </div>
                        ))}
                    </div>
                )}

                {callType === 'video' ? (
                    <div className="video-grid">
                        {/* Screen Share Display */}
                        {isSomeoneSharing && (
                            <div className="screen-share-container">
                                <ContentShare className="screen-share-video" />
                                <div className="screen-share-label">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                                        <line x1="8" y1="21" x2="16" y2="21" />
                                        <line x1="12" y1="17" x2="12" y2="21" />
                                    </svg>
                                    {isLocalUserSharing ? t('aiChat.calls.youAreSharing') : t('aiChat.calls.screenShare')}
                                </div>
                            </div>
                        )}

                        {/* Local video (picture-in-picture style) */}
                        <div className={`local-video-pip ${isSomeoneSharing ? 'screen-share-mode' : ''}`}>
                            <LocalVideo />
                            {!localVideoEnabled && (
                                <div className="video-off-overlay">
                                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M1 1l22 22M21 21H3a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                                        <path d="M23 7l-7 5 7 5V7z" />
                                    </svg>
                                    <span>{t('aiChat.calls.cameraOff')}</span>
                                </div>
                            )}
                        </div>

                        {/* Remote videos */}
                        {!isSomeoneSharing && (
                            <VideoTileGrid
                                className="remote-video-grid"
                                noRemoteVideoView={
                                    <div className="no-remote-video">
                                        {isRinging ? (
                                            <div className="calling-animation">
                                                <div className="calling-pulse" />
                                                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                                                </svg>
                                                <span>{t('aiChat.calls.callingName', { name: initiatorName || t('aiChat.calls.participant') })}</span>
                                            </div>
                                        ) : remoteParticipants.length > 0 ? (
                                            <div className="remote-participants-no-video">
                                                {remoteParticipants.map((p) => {
                                                    const handIndex = raisedHands.findIndex(h => String(h.userId) === String(p.externalUserId));
                                                    const isSpeaking = activeSpeakerId === p.attendeeId;
                                                    return (
                                                        <div key={p.attendeeId} className={`remote-participant-avatar${isSpeaking ? ' is-speaking' : ''}`}>
                                                            <div className="participant-avatar-circle">
                                                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                                                    <circle cx="12" cy="7" r="4" />
                                                                </svg>
                                                                {handIndex >= 0 && (
                                                                    <div className="avatar-hand-indicator">
                                                                        <span>{'\u270B'}</span>
                                                                        <span className="avatar-hand-order">#{handIndex + 1}</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <span className="participant-name">{p.name}</span>
                                                            <span className="participant-camera-off">{t('aiChat.calls.cameraOffShort')}</span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <span>{t('aiChat.calls.waitingForParticipants')}</span>
                                        )}
                                    </div>
                                }
                            />
                        )}
                    </div>
                ) : (
                    /* Audio-only call view */
                    <div className="audio-call-view">
                        <div className="audio-call-avatar">
                            <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                <circle cx="12" cy="7" r="4" />
                            </svg>
                        </div>
                        <div className="audio-call-info">
                            {isRinging ? (
                                <>
                                    <span className="audio-call-status">{t('aiChat.calls.calling')}</span>
                                    <div className="audio-wave-animation">
                                        <span /><span /><span /><span /><span />
                                    </div>
                                </>
                            ) : (
                                <>
                                    <span className="audio-call-status">{t('aiChat.header.connected')}</span>
                                    <span className="audio-call-duration">{formattedDuration}</span>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Participants Panel */}
            <ParticipantsPanel
                isOpen={showParticipants}
                onClose={() => setShowParticipants(false)}
                onInvite={handleInviteFromPanel}
                raisedHands={raisedHands}
                activeSpeakerId={activeSpeakerId}
            />

            {/* Camera / microphone / speaker pickers */}
            <DeviceSettingsPanel
                isOpen={showDeviceSettings}
                onClose={() => setShowDeviceSettings(false)}
                callType={callType}
            />

            {/* Controls */}
            <CallControls
                callType={callType}
                isVideoEnabled={isVideoEnabled}
                isAudioEnabled={!muted}
                isScreenSharing={isLocalUserSharing}
                isHandRaised={raisedHands.some(h => String(h.userId) === String(userId))}
                onToggleVideo={handleToggleVideo}
                onToggleAudio={handleToggleAudio}
                onToggleScreenShare={handleToggleScreenShare}
                onToggleHandRaise={handleToggleHandRaise}
                onEndCall={handleEndCall}
                onToggleParticipants={toggleParticipantsPanel}
                onInvite={openInviteModal}
                onToggleDeviceSettings={toggleDeviceSettings}
                onToggleFullscreen={fullscreenSupported ? toggleFullscreen : null}
                isFullscreen={isFullscreen}
                deviceSettingsOpen={showDeviceSettings}
                participantsOpen={showParticipants}
                participantCount={participantCount}
            />

            {/* Invite Modal */}
            <InviteToCallModal
                isOpen={showInviteModal}
                onClose={() => setShowInviteModal(false)}
                sessionId={sessionId}
                callId={callId}
                currentParticipants={currentParticipants}
                socket={socket}
                userId={userId}
            />
        </div>
    );
}

export default VideoCallContent;
